import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { PARAMETER_NATURES_DATA } from '../../../referencial/components/parameter/constants/ParameterConstants'
import { nbPerPageLabel } from '../../../referencial/constants/ReferencialConstants'
import { getLocalizationLabel, getLocalizationPicto, ResultWithStatus } from '../../../utils/AnalyseUtils'
import { getDate } from '../../../utils/DateUtil'
import { getLabel, getObjectLabel } from '../../../utils/StoreUtils'
import TooltipAnalyse from 'quality/components/qualityComponents/TooltipAnalyse'
import SelectionSelect from 'components/forms/specific/SelectionSelect'
import useActions from 'utils/customHook/useActions'
import DtoOperation from 'quality/dto/operation/DtoOperation'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useListIndexed from 'utils/customHook/useListIndexed'
import { SELECTION } from 'quality/constants/QualityConstants'
import { ParameterGraphModal } from '../qualityComponents/ParameterGraph'
import { isUndefined, keys } from 'lodash'
import useBoolean from 'utils/customHook/useBoolean'
import DtoQualityThreshold from 'quality/dto/QualityThreshold/DtoQualityThreshold'
import { CardTable } from 'components/datatable/NewTable'
import { calculateThresholdsResult, getMarkerColor, isNotConform } from 'utils/QualityThresholdUtils'
import { Grid } from '@mui/material'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'

const DEFAULT_SELECTION = '-1'

// classement exclusif d'une analyse : la limite prime sur la référence
const CONFORMITY = {
    LIMIT: 'limit',
    REFERENCE: 'reference',
    COMPLIANT: 'compliant',
    UNCLASSIFIED: 'unclassified', // aucun seuil pour le paramètre dans les deux jeux
}

const getConformityLabel = conformity => {
    switch (conformity) {
        case CONFORMITY.LIMIT: return i18n.limitExceeded
        case CONFORMITY.REFERENCE: return i18n.referenceExceeded
        case CONFORMITY.UNCLASSIFIED: return i18n.unclassified
        default: return i18n.compliant
    }
}

const AEPoverviewPanel = ({
    filter = {},
    onExport = () => { },
    analysis = [],
    operations = [],
    thresholdList = [],
    referenceThresholds = [],
    limitThresholds = [],
}) => {
    const {
        qualitometers,
        producers,
        qualifications,
        status,
        parametersIndex,
        unitsIndex,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        producers: store.ContributorReducer.producers,
        qualifications: store.QualityReducer.qualifications,
        status: store.QualityReducer.status,
        parametersIndex: store.ParameterReducer.parametersIndex,
        unitsIndex: store.UnitReducer.unitsIndex,
    }), shallowEqual)

    const settingSelection = useApplicationSetting(`SELECTION_FILTERS_${SELECTION.PC}`)
    const accountSettingSelection = useAccountSetting(`SELECTION_FILTERS_${SELECTION.PC}`)
    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const {
        value: isOpen,
        setFalse: onClose,
        setTrue: onOpen,
    } = useBoolean(false)
    const [selectedParameter, setSelectedParameter] = useState()

    const [selection, setSelection] = useState(DEFAULT_SELECTION)
    const [selectionResults, setSelectionResults] = useState([])

    useEffect(() => {
        setSelection(accountSettingSelection ?? settingSelection ?? DEFAULT_SELECTION)
    }, [settingSelection, accountSettingSelection])

    const producersIndexed = useListIndexed(producers, 'id')
    const qualitometersIndexed = useListIndexed(qualitometers, 'id')
    const operationsIndexed = useListIndexed(operations, o => `${o.id}#${o.qualitometer}`)

    // chaque analyse est évaluée séparément sur le jeu de limite et sur le jeu de référence :
    // résultat, couleur et seuil affichés sont ceux du jeu qui la classe
    const filteredAnalysis = useMemo(() => {
        const selectionFilter = selection !== DEFAULT_SELECTION ? analysis.filter(({ parameter }) => selectionResults.includes(parameter)) : analysis
        return selectionFilter
            .filter(a => !!operationsIndexed[`${a.operation}#${a.qualitometer}`])
            .map(a => {
                const limitResult = calculateThresholdsResult(a, limitThresholds)
                const referenceResult = calculateThresholdsResult(a, referenceThresholds)
                if (!limitResult.threshold && !referenceResult.threshold) return { ...a, ...referenceResult, conformity: CONFORMITY.UNCLASSIFIED }
                if (isNotConform(a, limitResult.threshold)) return { ...a, ...limitResult, conformity: CONFORMITY.LIMIT }
                if (isNotConform(a, referenceResult.threshold)) return { ...a, ...referenceResult, conformity: CONFORMITY.REFERENCE }
                return { ...a, ...(referenceResult.threshold ? referenceResult : limitResult), conformity: CONFORMITY.COMPLIANT }
            })
    }, [analysis, limitThresholds, operationsIndexed, referenceThresholds, selection, selectionResults])

    const formatedAnalysis = useMemo(() => {
        return filteredAnalysis.map(a => {
            const parameter = parametersIndex[a.parameter]

            const { [a.unit]: { symbol } = {} } = unitsIndex

            const paramLabel = `${getObjectLabel(parameter, 'name')} ${(parameter?.code && showSandreCode) ? `[${parameter.code}]` : ''}`

            const producer = producersIndexed[a.producer]
            const producerLabel = producer?.mnemonique ?? producer?.name ?? i18n.unknownProducer

            const qualitometer = qualitometersIndexed[a.qualitometer]

            return {
                statusAndResult: {
                    value: (
                        <ResultWithStatus
                            qualification={a.qualification}
                            status={a.status}
                            result={a.value}
                            color={a.color}
                        />
                    ),
                    tooltip: (
                        <TooltipAnalyse
                            title={paramLabel}
                            analyse={a}
                            threshold={a.threshold}
                        />
                    ),
                },
                stationCode: { value: qualitometer?.code },
                station: { value: qualitometer?.name },
                producer: { value: producerLabel },
                date: { value: getDate(a.analysisDate) },
                parameter: { value: paramLabel },
                unit: { value: symbol },
                threshold: { value: a.threshold?.[`threshold${a.index}`] },
                nullValue: { value: getLocalizationPicto(a.localization) },
                parameterCode: a.parameter,
                conformity: a.conformity,
            }
        })
    }, [filteredAnalysis, parametersIndex, producersIndexed, qualitometersIndexed, unitsIndex, showSandreCode])

    useActions(() => ({
        exportList: [{
            onClick: () => {
                const data = filteredAnalysis.map(a => {
                    const parameter = parametersIndex[a.parameter]
                    const producer = producersIndexed[a.producer]
                    const producerLabel = producer?.mnemonique || producer?.name || i18n.unknownProducer

                    const qualitometer = qualitometersIndexed[a.qualitometer]

                    return {
                        year: { value: filter.year, format: '0', cellType: 'number' },
                        stationCode: { value: qualitometer?.code, cellType: 'right' },
                        station: { value: qualitometer?.name },
                        date: { value: getDate(a.analysisDate), cellType: 'date' },
                        parameterCode: { value: a.parameter, cellType: 'right' },
                        parameter: parametersIndex[a.parameter].shortLabel || parametersIndex[a.parameter].name,
                        codeUnit: { value: a.unit, cellType: 'right' },
                        unit: unitsIndex[a.unit]?.symbol,
                        result: { value: a.value, color: getMarkerColor(a.color), cellType: 'right' },
                        qualification: getLabel(qualifications, a.qualification),
                        status: getLabel(status, a.status),
                        producer: producerLabel,
                        nature: getLabel(PARAMETER_NATURES_DATA, parameter.typeParam),
                        threshold: { value: a.threshold?.[`threshold${a.index}`], cellType: 'number' },
                        conformity: { value: getConformityLabel(a.conformity) },
                        localisation: { value: getLocalizationLabel(a.localization) },
                    }
                })
                onExport({
                    data: data.length ? [{ ...data[0], headers: keys(data[0]) }, ...data.slice(1)] : [],
                    exportType: 'xlsx',
                    titleFile: i18n.noncompliances,
                })
            },
            label: i18n.excel,
        }],
    }), [formatedAnalysis, qualitometersIndexed, producersIndexed, parametersIndex])

    const selectedAnalysis = useMemo(() => {
        if (isUndefined(selectedParameter)) {
            return []
        }
        return analysis.filter(a => a.parameter === selectedParameter)
    }, [analysis, selectedParameter])

    return (
        <Grid container spacing={1} alignItems='center'>
            <Grid size={8}>
                <SelectionSelect
                    value={selection}
                    onChange={(res, s) => {
                        setSelection(s)
                        setSelectionResults(res)
                    }}
                />
            </Grid>
            {[CONFORMITY.LIMIT, CONFORMITY.REFERENCE, CONFORMITY.COMPLIANT, CONFORMITY.UNCLASSIFIED].map(conformity => (
                <Grid size={12} key={conformity}>
                    <CardTable
                        title={getConformityLabel(conformity)}
                        rows={formatedAnalysis.filter(row => row.conformity === conformity)}
                        headers={['stationCode', 'station', 'producer', 'date', 'parameter', 'statusAndResult', 'unit', 'threshold', 'nullValue']}
                        onClickRow={row => {
                            onOpen()
                            setSelectedParameter(row.parameterCode)
                        }}
                        rowsPerPageOptions={nbPerPageLabel}
                    />
                </Grid>
            ))}
            <ParameterGraphModal
                isOpen={isOpen}
                closeGraph={onClose}
                analysis={selectedAnalysis}
                thresholds={thresholdList}
            />
        </Grid>
    )
}

AEPoverviewPanel.propTypes = {
    filter: PropTypes.shape({
        referenceThreshold: PropTypes.number,
        limitThreshold: PropTypes.number,
        year: PropTypes.number,
    }),
    onExport: PropTypes.func,
    operations: PropTypes.arrayOf(PropTypes.instanceOf(DtoOperation)),
    analysis: PropTypes.arrayOf(PropTypes.shape({
        // DtoAnalysisLight
        // calculateThresholdsResult
        producer: PropTypes.number,
    })),
    thresholdList: PropTypes.arrayOf(PropTypes.instanceOf(DtoQualityThreshold)),
    referenceThresholds: PropTypes.arrayOf(PropTypes.instanceOf(DtoQualityThreshold)),
    limitThresholds: PropTypes.arrayOf(PropTypes.instanceOf(DtoQualityThreshold)),
}

export default AEPoverviewPanel